import type { ReactNode } from 'react';
import type React from 'react';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { Form, FormControl } from 'react-bootstrap';
import { toast } from 'react-toastify';
import type { FormikHelpers } from 'formik/dist/types';
import { Button } from '@itandi/itandi-bb-ui';
import SettingStyles from '../SettingStyle.module.scss';
import type { VendorUserUpdatePasswordParam } from '~/models/VendorUser';
import { FormGroup } from '~/components/common/parts/form/FormGroup';
import { changePassword } from '~/utils/api/vendors/VendorCompany';
import { isSuccess } from '~/utils/api/api_handler';
import type { VendorCompanyUpdatePasswordParam } from '~/models/VendorCompany';
import {
  commonNewPasswordValidationSchema,
  requireOldPasswordValidationSchema
} from '~/models/VendorCompany';
import { useSessions } from '~/hooks/vendors/useSessions';

export const AccountSettingPassword: React.FC = () => {
  const { data: session } = useSessions().index();
  const handleSave = async (
    passwords: VendorCompanyUpdatePasswordParam,
    { resetForm }: FormikHelpers<VendorUserUpdatePasswordParam>
  ): Promise<void> => {
    const result = await changePassword(passwords);
    if (isSuccess(result)) {
      resetForm();
      toast.success('パスワードを変更しました');
      return;
    }
    toast.error(result.message ?? 'エラーが発生しました');
  };
  return (
    <div>
      <h4 className={SettingStyles.SettingSubTitle}>パスワードの変更</h4>
      <Formik
        initialValues={{
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        }}
        onSubmit={handleSave}
        validationSchema={
          session?.admin
            ? commonNewPasswordValidationSchema
            : requireOldPasswordValidationSchema
        }
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          dirty,
          errors,
          touched
        }: FormikProps<VendorCompanyUpdatePasswordParam>): ReactNode => (
          <div className={SettingStyles.MiddleSizeBlock}>
            <FormGroup>
              <label className={SettingStyles.NoBackgroundLabel}>
                現在のパスワード
              </label>
              <FormControl
                disabled={session ? session.admin : false}
                isInvalid={touched.oldPassword && !!errors.oldPassword}
                name="oldPassword"
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                value={values.oldPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.oldPassword}
              </Form.Control.Feedback>
            </FormGroup>
            <FormGroup>
              <label className={SettingStyles.NoBackgroundLabel}>
                新しいパスワード
              </label>
              <FormControl
                isInvalid={touched.newPassword && !!errors.newPassword}
                name="newPassword"
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                value={values.newPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.newPassword}
              </Form.Control.Feedback>
            </FormGroup>
            <FormGroup>
              <label className={SettingStyles.NoBackgroundLabel}>
                新しいパスワード（確認用）
              </label>
              <FormControl
                isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                name="confirmPassword"
                onBlur={handleBlur}
                onChange={handleChange}
                type="password"
                value={values.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword}
              </Form.Control.Feedback>
            </FormGroup>
            <Button
              disabled={!dirty}
              onClick={(): void => handleSubmit()}
              variant="primary"
            >
              パスワードを変更
            </Button>
          </div>
        )}
      </Formik>
    </div>
  );
};
